/**
 * Notification Panel Component
 * Displays wallet tracker alert notifications
 */

import { useAlertStore } from '../../store/alertStore';
import { Icons } from '../Icons';
import { formatDistanceToNow } from 'date-fns';

export function NotificationPanel() {
  const {
    notifications,
    showNotifications,
    setShowNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    removeNotification,
    getUnreadCount,
  } = useAlertStore();

  const unreadCount = getUnreadCount();

  if (!showNotifications) {
    return null;
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'swap':
        return <Icons.ArrowRightLeft size={16} />;
      case 'transfer_in':
        return <Icons.ArrowRight size={16} style={{ transform: 'rotate(-90deg)' }} />;
      case 'transfer_out':
        return <Icons.ArrowRight size={16} style={{ transform: 'rotate(90deg)' }} />;
      default:
        return <Icons.Activity size={16} />;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 999,
        }}
        onClick={() => setShowNotifications(false)}
      />

      {/* Notification Panel */}
      <div
        style={{
          position: 'fixed',
          top: '60px',
          right: '20px',
          width: '400px',
          maxWidth: '90vw',
          maxHeight: '600px',
          backgroundColor: 'var(--background-primary)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icons.Bell size={18} />
            <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>
              Notifications
            </h3>
            {unreadCount > 0 && (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  backgroundColor: 'var(--color-primary)',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '10px',
                }}
              >
                {unreadCount}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            {unreadCount > 0 && (
              <button
                className="btn-ghost"
                onClick={markAllNotificationsAsRead}
                style={{ fontSize: '12px', padding: '4px 8px' }}
                title="Mark all as read"
              >
                <Icons.Check size={14} />
              </button>
            )}
            <button
              className="btn-ghost"
              onClick={() => setShowNotifications(false)}
              style={{ fontSize: '12px', padding: '4px 8px' }}
            >
              <Icons.X size={14} />
            </button>
          </div>
        </div>

        {/* Notification List */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '8px',
          }}
        >
          {notifications.length === 0 ? (
            /* Empty State */
            <div
              style={{
                padding: '40px 20px',
                textAlign: 'center',
              }}
            >
              <Icons.Bell size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>
                No notifications yet
              </p>
              <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '8px' }}>
                You'll be notified when your tracked wallets make transactions
              </p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                style={{
                  padding: '12px',
                  marginBottom: '8px',
                  backgroundColor: notification.read
                    ? 'var(--background-secondary)'
                    : 'var(--background-primary)',
                  border: `1px solid ${
                    notification.read ? 'var(--border-color)' : 'var(--color-primary)'
                  }`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => {
                  if (!notification.read) {
                    markNotificationAsRead(notification.id);
                  }
                }}
              >
                <div style={{ display: 'flex', gap: '12px', alignItems: 'start' }}>
                  <div
                    style={{
                      marginTop: '2px',
                      color: notification.read
                        ? 'var(--text-tertiary)'
                        : 'var(--color-primary)',
                    }}
                  >
                    {getNotificationIcon(notification.type)}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Wallet Label */}
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        marginBottom: '4px',
                        color: 'var(--text-primary)',
                      }}
                    >
                      {notification.walletLabel || `${notification.walletAddress.slice(0, 8)}...`}
                    </div>

                    {/* Message */}
                    <div
                      style={{
                        fontSize: '13px',
                        color: 'var(--text-secondary)',
                        marginBottom: '6px',
                      }}
                    >
                      {notification.message}
                    </div>

                    {/* Value if present */}
                    {notification.value && (
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: 'var(--color-primary)',
                          marginBottom: '6px',
                        }}
                      >
                        ${notification.value.toLocaleString()}
                      </div>
                    )}

                    {/* Timestamp */}
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                      {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
                    </div>

                    {/* Transaction Link */}
                    {notification.transactionHash && (
                      <a
                        href={`https://etherscan.io/tx/${notification.transactionHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          fontSize: '12px',
                          color: 'var(--color-primary)',
                          marginTop: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        View on Etherscan
                        <Icons.ExternalLink size={12} />
                      </a>
                    )}
                  </div>

                  {/* Remove Button */}
                  <button
                    className="btn-ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeNotification(notification.id);
                    }}
                    style={{
                      padding: '4px',
                      minWidth: 'auto',
                      opacity: 0.6,
                    }}
                    title="Dismiss"
                  >
                    <Icons.X size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
